<template>
  <div class="home">
    <!-- =========================
    ✅ Section 0: Sequence.js (1:1)
    ========================= -->
    <section class="home-seq">
      <!-- ✅ 背景改用 inline style，避免 SCSS url 在某些環境爆炸 -->
      <div
        id="sequence"
        class="seq"
        :class="{ 'is-phone': isPhone }"
        ref="sequence"
        :style="{ backgroundImage: `url(${seqBg})` }"
      >
        <div class="seq-screen">
          <ul class="seq-canvas">
            <li
              v-for="(s, idx) in slides"
              :key="idx"
              :class="{ 'seq-in': idx === currentSeqIndex }"
              :aria-hidden="idx !== currentSeqIndex"
            >
              <div class="seq-model" :class="{ 'is-bleed': s.bleed }">
                <a v-if="s.link" :href="s.link" target="_blank" rel="noopener">
                  <img
                    data-seq
                    :src="s.big"
                    :alt="s.alt"
                    :class="{ 'is-cover': s.fit === 'cover' }"
                  />
                </a>
                <img
                  v-else
                  data-seq
                  :src="s.big"
                  :alt="s.alt"
                  :class="{ 'is-cover': s.fit === 'cover' }"
                />
              </div>

              <div class="seq-title">
                <h2 data-seq>{{ s.h2 }}</h2>
                <h3 data-seq v-html="s.h3"></h3>
              </div>
            </li>
          </ul>
        </div>

        <fieldset class="seq-nav" aria-controls="sequence" aria-label="Slider buttons">
          <button type="button" class="seq-prev" aria-label="Previous" @click="previousSeqSlide"></button>
          <button type="button" class="seq-next" aria-label="Next" @click="nextSeqSlide"></button>
        </fieldset>

        <ul
          role="navigation"
          aria-label="Pagination"
          class="seq-pagination"
        >
          <li
            v-for="(s, idx) in slides"
            :key="`p-${idx}`"
            :class="{ 'seq-current': idx === currentSeqIndex }"
          >
            <button
              v-if="s.thumb"
              type="button"
              :title="`Go to slide ${idx + 1}`"
              :aria-current="idx === currentSeqIndex ? 'true' : undefined"
              @click="goToSeqSlide(idx)"
            >
              <img :src="s.thumb" :alt="`thumb ${idx + 1}`" />
            </button>
          </li>
        </ul>
      </div>
    </section>


    <!-- =========================
     ✅ Section 1: About Intro（左圖右文）
     ✅ 斷續弧段 + 多層 + 順逆時針 + 光暈光點（疊在左圖上）
     ========================= -->
    <section class="home-hero">
      <div class="container-sm py-5">
        <div class="row align-items-center g-4">
          <!-- Left Image -->
          <div class="col-12 col-lg-6">
            <div class="hero-img-wrap">
              <img
                class="hero-img"
                src="@/assets/images/about/myguard_about_01.jpg"
                alt="MYGUARD About"
              />

              <!-- ✅ 疊在圖上的軌道層 -->
              <div class="hero-orbits on-image" aria-hidden="true">
                <!-- 多層：順時針/逆時針交錯 -->
                <div class="ring r1  cw  s1"><span class="glow-dot"></span></div>
                <div class="ring r2  ccw s2"><span class="glow-dot"></span></div>
                <div class="ring r3  cw  s3"><span class="glow-dot"></span></div>
                <!-- <div class="ring r4  ccw s4"><span class="glow-dot"></span></div>
                <div class="ring r5  cw  s5"><span class="glow-dot"></span></div>
                <div class="ring r6  ccw s6"><span class="glow-dot"></span></div> -->
                <!-- <div class="ring r7  cw  s7"><span class="glow-dot"></span></div>
                <div class="ring r8  ccw s8"><span class="glow-dot"></span></div>
                <div class="ring r9  cw  s9"><span class="glow-dot"></span></div>
                <div class="ring r10 ccw s10"><span class="glow-dot"></span></div> -->
              </div>
            </div>
          </div>

          <!-- Right Text -->
          <div class="col-12 col-lg-6">
            <div
              class="hero-text"
              :key="`hero-text-${$i18n.locale}`"
              :class="{ 'is-en': $i18n.locale === 'en' }"
              data-aos="fade-left"
              data-aos-delay="80"
            >
              <h2 class="hero-title">
                <span ref="heroTitleLine1" class="hero-title-line">
                  {{ $t('Home.title1-1') }}
                </span>
                <span ref="heroTitleLine2" class="hero-title-line">
                  {{ $t('Home.title1-2') }}
                </span>
              </h2>
              <p class="hero-subtitle" v-html="$t('Home.subtitle')"></p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 2 -->
    <section class="home-text-band">
      <!-- ✅ 上半段：背景圖區（桌機/手機都顯示標題；桌機才顯示內文疊字） -->
      <div class="band-hero">
        <div class="container-sm py-5">
          <h2
            class="band-title text-center"
            data-aos="fade-up"
            data-aos-delay="0"
          >
            <!-- 桌機標題（純文字） -->
            <span class="d-none d-sm-inline">
              {{ $t("Home.title2") }}
            </span>

            <!-- 手機標題（支援 <br>） -->
            <span
              class="d-inline d-sm-none"
              v-html="$t('Home.title2-phone')"
            ></span>
          </h2>



          <!-- ✅ 桌機維持：文字疊在背景圖上 -->
          <div
            class="band-subtitle mx-auto"
            v-html="$t('Home.subtitle2')"
            data-aos="fade-up"
            data-aos-delay="120"
          ></div>

        </div>
      </div>

      <!-- ✅ 手機專用：背景圖「下面」的灰色區塊（黑字） -->
      <div class="band-body-mobile d-block d-sm-none">
        <div class="container-sm py-4">
          <div class="band-subtitle-mobile mx-auto" v-html="$t('Home.subtitle2')"></div>
        </div>
      </div>
    </section>

    <!-- Section 3: 多元專業領域布局 -->
    <section class="home-domains">
      <div class="container-sm py-5">

        <!-- ✅ 標題：永遠在最上方 -->
        <h2 class="domains-title text-center mb-3">
          {{ $t("Home.title3") }}
        </h2>

        <div class="domains-wrap">

          <!-- ✅ 手機：上方文字框（只在點上排時存在） -->
          <div
            v-if="isPhone && selectedDomain < 4"
            class="domains-pop-box is-top"
            :style="{ '--popColor': domains[selectedDomain]?.color }"
          >
            <div
              class="domains-pop-inner"
              v-html="$t(`Home.subtitle3-${selectedDomain + 1}`)"
            ></div>
          </div>

          <!-- ✅ 圈圈按鈕群 -->
          <div class="domains-grid">
            <div
              v-for="(d, i) in domains"
              :key="d.key"
              class="domain-item"
              :class="{
                active: selectedDomain === i,
                'label-above': isPhone && i < 4,
                'label-below': !isPhone || i >= 4
              }"
              :style="{
                '--activeColor': d.color,
                '--maskSrc': `url(${isPhone ? (i < 4 ? maskUp : maskDown) : maskDown})`
              }"
            >
              <!-- 常駐標題 -->
              <div class="domain-label">
                {{ $t(`Home.title3-${i + 1}`) }}
              </div>

              <button
                class="domain-btn"
                type="button"
                @click="selectedDomain = i"
              >
                <img
                  class="domain-img"
                  :src="isPhone ? (d.phoneSrc || d.webSrc) : d.webSrc"
                  :alt="d.key"
                  loading="lazy"
                  decoding="async"
                />
                <span class="domain-color" aria-hidden="true"></span>
              </button>

              <!-- ✅ 桌機：內文跟著選到的那顆 -->
              <transition name="fade-slide-up">
                <div
                  v-show="!isPhone && selectedDomain === i"
                  class="domain-pop"
                >
                  <div
                    class="domain-pop-inner"
                    v-html="$t(`Home.subtitle3-${i + 1}`)"
                  ></div>
                </div>
              </transition>
            </div>
          </div>

          <!-- ✅ 手機：下方文字框（只在點下排時存在） -->
          <div
            v-if="isPhone && selectedDomain >= 4"
            class="domains-pop-box is-bottom"
            :style="{ '--popColor': domains[selectedDomain]?.color }"
          >
            <div
              class="domains-pop-inner"
              v-html="$t(`Home.subtitle3-${selectedDomain + 1}`)"
            ></div>
          </div>

        </div>
      </div>
    </section>


    <!-- Section 4: Timeline (遠京歷程) -->
    <section class="home-timeline bg-light">
      <div class="container-sm py-5">
        <h2 id="history-anchor" class="timeline-title text-center">
          {{ $t("Home.history") }}
        </h2>

        <div class="timeline mx-auto">
          <div
            v-for="(item, idx) in timeline"
            :key="item.key || item.year"
            class="timeline-item"
          >
            <div class="timeline-marker">
              <div class="timeline-year">{{ item.year }}</div>
              <div
                v-if="idx < timeline.length - 1"
                class="timeline-line"
                aria-hidden="true"
              ></div>
            </div>

            <div class="timeline-content">
              <div
                class="timeline-text"
                v-html="$t(`Home.${item.key || item.year}`)"
                data-aos="fade-left"
                data-aos-duration="600"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 聯絡我們（保持原樣，不動） -->
    <div id="contact">
      <div class="container py-5 text-start">

        <h3 class="text-black border-bottom border-2">
          遠京企業股份有限公司 MYGUARD CO., LTD.
        </h3>

        <div class="row">
          <!-- 左側 -->
          <div class="col-lg-6 col-sm-10">
            <br />
            <p class="fw-bold">{{ $t("contact.country") }}</p>
            <p>
              <span class="fw-bold">{{ $t("contact.address") }}: </span>
              {{ $t("contact.addressV") }}
            </p>
            <p>
              <span class="fw-bold">{{ $t("contact.phone") }}: </span>
              <a href="tel:+886-3-327-0080">+886 3 327-0080</a>
            </p>
            <p>
              <span class="fw-bold">{{ $t("contact.email") }}: </span>
              <a href="mailto:info@myguard.com">info@myguard.com</a>
            </p>
            <br />
          </div>

          <!-- 右側 -->
          <div class="col-lg-6 col-sm-10">
            <br />
            <p class="fw-bold">Taiwan</p>
            <p>
              <span class="fw-bold">{{ $t("contact.address") }}: </span>
              3F.-2, No.146, Wenxing Rd., Guishan, Taoyuan City 33377, Taiwan
            </p>
            <p>
              <span class="fw-bold">{{ $t("contact.phone") }}: </span>
              <a href="tel:+886-3-327-0080">+886 3 327-0080</a>
            </p>
            <p>
              <span class="fw-bold">{{ $t("contact.email") }}: </span>
              <a href="mailto:info@myguard.com">info@myguard.com</a>
            </p>
          </div>
        </div>

        <!-- Google Map -->
        <iframe
          class="w-100 mt-3"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28913.318916081163!2d121.3655983!3d25.062400699999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442a77f1ea5bfdb%3A0xf37f0cec253def74!2z6YGg5Lqs5LyB5qWt6IKh5Lu95pyJ6ZmQ5YWs5Y-4!5e0!3m2!1szh-TW!2stw!4v1675846670497!5m2!1szh-TW!2stw"
          height="450"
          style="border: 0"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
// ✅ Section 0 images (Sequence) - Web
import seqBgWeb from "@/assets/images/about/web/bg.jpg";
import b01Web from "@/assets/images/about/web/b_01.png";
import b02Web from "@/assets/images/about/web/b_02.png";
import b03Web from "@/assets/images/about/web/b_03.png";
// import b04Web from "@/assets/images/about/web/b_04.png";
// import b05Web from "@/assets/images/about/web/b_05.png";
// import b06Web from "@/assets/images/about/web/b_06.jpg";
import b07Web from "@/assets/images/about/web/b_07.png";

import s01Web from "@/assets/images/about/web/s_01.png";
import s02Web from "@/assets/images/about/web/s_02.png";
import s03Web from "@/assets/images/about/web/s_03.png";
// import s04Web from "@/assets/images/about/web/s_04.png";
// import s05Web from "@/assets/images/about/web/s_05.png";
import s07Web from "@/assets/images/about/web/s_07.png";


// ✅ Section 0 images (Sequence) - Phone
import seqBgPhone from "@/assets/images/about/phone/bg.jpg";
import b01Phone from "@/assets/images/about/phone/b_01.png";
import b02Phone from "@/assets/images/about/phone/b_02.png";
import b03Phone from "@/assets/images/about/phone/b_03.png";
// import b04Phone from "@/assets/images/about/phone/b_04.png";
// import b05Phone from "@/assets/images/about/phone/b_05.png";
// import b06Phone from "@/assets/images/about/phone/b_06.jpg";
import b07Phone from "@/assets/images/about/phone/b_07.png";

// web images 02~09
import web02 from "@/assets/images/about/web/myguard_about_02.png";
import web03 from "@/assets/images/about/web/myguard_about_03.png";
import web04 from "@/assets/images/about/web/myguard_about_04.png";
import web05 from "@/assets/images/about/web/myguard_about_05.png";
import web06 from "@/assets/images/about/web/myguard_about_06.png";
import web07 from "@/assets/images/about/web/myguard_about_07.png";
import web08 from "@/assets/images/about/web/myguard_about_08.png";
import web09 from "@/assets/images/about/web/myguard_about_09.png";

// phone images 02~05 (arrow up)
import phone02 from "@/assets/images/about/phone/myguard_about_02.png";
import phone03 from "@/assets/images/about/phone/myguard_about_03.png";
import phone04 from "@/assets/images/about/phone/myguard_about_04.png";
import phone05 from "@/assets/images/about/phone/myguard_about_05.png";

// mask images
import maskDown from "@/assets/images/about/mask_down.png";
import maskUp from "@/assets/images/about/mask_up.png";

export default {
  name: "Home",
  data() {
    return {
      seqAutoTimer: null,
      seqAutoDelay: 3000, // ✅ 3 秒
      currentSeqIndex: 0,

      // ✅ 放兩套給 computed 用
        seqBgWeb,
        seqBgPhone,

        slidesWeb: [
// { big: b06Web, thumb: null, h2: "Workshop", h3: "", alt: "Workshop", fit: "cover" },
// { big: b05Web, thumb: s05Web, h2: "AMWC", h3: "", alt: "AMWC", fit: "cover", bleed: true },
          { big: b03Web, thumb: s03Web, h2: "TSN", h3: "", alt: "TSN", link: "https://tsklab.com.tw/product/TskSelectNeedle" },
          { big: b07Web, thumb: s07Web, h2: "TSY", h3: "", alt: "TSY", link: "https://tsklab.com.tw/product/TSKSyringe" },
          { big: b02Web, thumb: s02Web, h2: "STERiJECT", h3: "", alt: "STERiJECT", link: "https://tsklab.com.tw/product/STERiJECT" },
          { big: b01Web, thumb: s01Web, h2: "STERiGLIDE", h3: "", alt: "STERiGLIDE", link: "https://tsklab.com.tw/product/STERiGLIDE" },
        ],

        slidesPhone: [
// { big: b06Phone, thumb: null, h2: "Workshop", h3: "", alt: "Workshop", fit: "cover" },
// { big: b05Phone, thumb: s05Web, h2: "AMWC", h3: "", alt: "AMWC", fit: "cover", bleed: true },
          { big: b03Phone, thumb: s03Web, h2: "TSN", h3: "", alt: "TSN", link: "https://tsklab.com.tw/product/TskSelectNeedle" },
          { big: b07Phone, thumb: s07Web, h2: "TSY", h3: "", alt: "TSY", link: "https://tsklab.com.tw/product/TSKSyringe" },
          { big: b02Phone, thumb: s02Web, h2: "STERiJECT", h3: "", alt: "STERiJECT", link: "https://tsklab.com.tw/product/STERiJECT" },
          { big: b01Phone, thumb: s01Web, h2: "STERiGLIDE", h3: "", alt: "STERiGLIDE", link: "https://tsklab.com.tw/product/STERiGLIDE" },
        ],

      selectedDomain: 0,
      isPhone: false,
      maskDown,
      maskUp,
      domains: [
        { key: "d02", webSrc: web02, phoneSrc: phone02, color: "#170579" },
        { key: "d03", webSrc: web03, phoneSrc: phone03, color: "#173ED1" },
        { key: "d04", webSrc: web04, phoneSrc: phone04, color: "#3477D4" },
        { key: "d05", webSrc: web05, phoneSrc: phone05, color: "#5CC7D6" },
        { key: "d06", webSrc: web06, phoneSrc: null, color: "#61D1B2" },
        { key: "d07", webSrc: web07, phoneSrc: null, color: "#62D382" },
        { key: "d08", webSrc: web08, phoneSrc: null, color: "#8AD240" },
        { key: "d09", webSrc: web09, phoneSrc: null, color: "#B8D042" },
      ],

      timeline: [
        { year: "1998" },
        { year: "2003" },
        { year: "2009" },
        { year: "2015" },
        { year: "2019" },
        { year: "2020" },
        { year: "2021" },
        { year: "2022" },
        { year: "2023" },
        { year: "2023", key: "2023-tsk" },
        { year: "2025" },
        { year: "2026" },
      ],
    };
  },
  computed: {
    seqBg() {
      return this.isPhone ? this.seqBgPhone : this.seqBgWeb;
    },
    slides() {
      return this.isPhone ? this.slidesPhone : this.slidesWeb;
    },
  },

  mounted() {
    this.updateIsPhone();
    this.$nextTick(() => this.fitHeroTitleLines());

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.fitHeroTitleLines());
    }

    window.addEventListener("resize", this.updateIsPhone);
    this.applyLangClass();

    this.startSeqAutoplay();
  },
  watch: {
    "$i18n.locale"() {
      const y = window.scrollY;
      const x = window.scrollX;

      this.applyLangClass();

      this.$nextTick(() => {
        this.fitHeroTitleLines();

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, left: x, behavior: "auto" });

            // ✅ 捲動回來後，再刷新 AOS（讓 inView 判斷準）
            this.refreshAOS();
          });
        });
      });
    }
  },


  beforeUnmount() {
    window.removeEventListener("resize", this.updateIsPhone);

    this.stopSeqAutoplay();
  },

  methods: {
    startSeqAutoplay() {
      this.stopSeqAutoplay();
      this.seqAutoTimer = window.setInterval(() => {
        this.setSeqIndex(this.currentSeqIndex + 1);
      }, this.seqAutoDelay);
    },

    stopSeqAutoplay() {
      if (this.seqAutoTimer) {
        clearInterval(this.seqAutoTimer);
        this.seqAutoTimer = null;
      }
    },

    setSeqIndex(index) {
      const total = this.slides.length;
      if (!total) return;
      this.currentSeqIndex = (index + total) % total;
    },

    previousSeqSlide() {
      this.setSeqIndex(this.currentSeqIndex - 1);
      this.startSeqAutoplay();
    },

    nextSeqSlide() {
      this.setSeqIndex(this.currentSeqIndex + 1);
      this.startSeqAutoplay();
    },

    goToSeqSlide(index) {
      this.setSeqIndex(index);
      this.startSeqAutoplay();
    },

    refreshAOS() {
      const AOS = window.AOS;
      if (!AOS) return;

      const forceInView = () => {
        const els = document.querySelectorAll("[data-aos]");
        const vh = window.innerHeight || document.documentElement.clientHeight;

        els.forEach((el) => {
          const r = el.getBoundingClientRect();
          const inView = r.top < vh * 0.98 && r.bottom > vh * 0.02;

          el.classList.add("aos-init");
          if (inView) el.classList.add("aos-animate");
          // ❌ 不要再 remove，避免把 once 的元素變永久透明
        });
      };


      // ✅ 兩段 RAF：確保你 fitHeroTitleLines、v-html、字型排版都落地
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          AOS.refreshHard();

          // ✅ 再晚一拍：有些瀏覽器字型/排版會在 refreshHard 後還抖一下
          setTimeout(() => {
            forceInView();
          }, 60);
        });
      });
    },

    
    applyLangClass() {
      const el = document.documentElement; // <html>
      el.classList.toggle("lang-en", this.$i18n?.locale === "en");
    },
    updateIsPhone() {
      this.isPhone = window.innerWidth <= 576;
      this.$nextTick(() => this.fitHeroTitleLines());
    },

    fitHeroTitleLines() {
      if (this.$i18n?.locale === "en") {
        const line1 = this.$refs.heroTitleLine1;
        const line2 = this.$refs.heroTitleLine2;
        if (line1) line1.style.setProperty("--ls", "0px");
        if (line2) line2.style.setProperty("--ls", "0px");
        return;
      }
      
      const line1 = this.$refs.heroTitleLine1;
      const line2 = this.$refs.heroTitleLine2;
      if (!line1 || !line2) return;

      const container = line1.closest(".hero-text");
      if (!container) return;

      const targetW = container.clientWidth;

      // 手機字距上限小一點，避免像「字在分手」
      const cap = this.isPhone ? 10 : 24;

      this.stretchLineToWidth(line1, targetW, cap);
      this.stretchLineToWidth(line2, targetW, cap);
    },

    stretchLineToWidth(el, targetW, cap = 24) {
      const text = (el.textContent || "").replace(/\s+/g, "");
      const n = text.length;
      if (n <= 1) return;

      el.style.setProperty("--ls", "0px");

      // 量自然寬度：用 inline-block + auto
      const prevDisplay = el.style.display;
      const prevWidth = el.style.width;

      el.style.display = "inline-block";
      el.style.width = "auto";
      const textW = el.getBoundingClientRect().width;

      // 還原
      el.style.display = prevDisplay || "block";
      el.style.width = prevWidth || "100%";

      if (textW >= targetW - 1) {
        el.style.setProperty("--ls", "0px");
        return;
      }

      const extra = targetW - textW;
      const lsPx = extra / (n - 1);
      el.style.setProperty("--ls", `${Math.min(lsPx, cap)}px`);
    },
  },
};
</script>

<style lang="scss">
  html, body {
  width: 100%;
  overflow-x: hidden;
}
/* =========================
   ✅ English: unify Section 2 & 3 title size
   ========================= */
.lang-en .home-text-band .band-title,
.lang-en .home-domains .domains-title {
  font-size: clamp(22px, 1.6vw + 12px, 28px) !important; /* ✅ 同一套 */
  font-weight: 900; /* 你原本 S2 是 900，S3 是 800，英文統一更有品牌感 */
  letter-spacing: 0.02em; /* 英文微微開一點，比較精緻 */
  line-height: 1.2;
}
.lang-en .timeline-title {
  font-size: clamp(22px, 1.6vw + 12px, 28px) !important;
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1.2;
}
/* =========================
   ✅ Section 0: 先只看圖片（隱藏標題文字）
   ========================= */
.home-seq .seq .seq-title {
  display: none !important;
}

/* 保險：如果你怕某些版型還留空間，順便把相關文字元素也關掉 */
.home-seq .seq .seq-title h2,
.home-seq .seq .seq-title h3 {
  display: none !important;
}

/* =========================
   ✅ Section 0: Sequence.js theme (1:1, scoped-ish)
   ========================= */
@import url(https://fonts.googleapis.com/css?family=Oxygen:700|Source+Sans+Pro:300);

.home-seq {
  /* 這層只是隔離用 */
}

.home-seq,
.home-seq .seq {
  /* 原本是 body/html/seq，這裡只限定在 section0 範圍 */
}

.home-seq .seq {
  position: relative;
  background: #D9D9D9 no-repeat;
  background-position: 0% 100%;
  background-size: cover;
  height: 360px;
  width: 100%;
  max-width: 1280px;
  overflow: hidden;
  margin: 0 auto;
  padding: 0;
}

.home-seq .seq .seq-preloader {
  background: #D9D9D9;
}

.home-seq .seq .seq-screen,
.home-seq .seq .seq-canvas,
.home-seq .seq .seq-canvas > * {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  list-style: none;
}

.home-seq .seq .seq-canvas > * {
  position: absolute;
  pointer-events: none;
}

.home-seq .seq .seq-canvas > .seq-in {
  pointer-events: auto;
}

.home-seq .seq.seq-fallback .seq-canvas {
  max-width: 1280px;
}

.home-seq .seq .seq-pagination {
  transition: opacity 0.6s ease, visibility 0.6s ease;
}

.home-seq .seq .seq-pagination.hide-pagination {
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none;
}

.home-seq .seq.seq-fallback .seq-pagination li {
  position: relative;
}

.home-seq .seq.seq-fallback .seq-pagination li img {
  border: none;
}

.home-seq .seq.seq-fallback .seq-pagination li a {
  display: block;
  width: 100%;
  height: 100%;
}

.home-seq .seq.seq-fallback .seq-next:focus,
.home-seq .seq.seq-fallback .seq-next:hover,
.home-seq .seq.seq-fallback .seq-prev:focus,
.home-seq .seq.seq-fallback .seq-prev:hover,
.home-seq .seq.seq-fallback .seq-pagination li:focus,
.home-seq .seq.seq-fallback .seq-pagination li:hover {
  top: -4px;
}

.home-seq .seq .seq-canvas {
  max-width: 1280px;
  overflow: hidden;
  position: relative;
  display: block;
  margin: 0 auto;
}

.home-seq .seq .seq-nav {
  display: block !important;
}
.home-seq .seq .seq-pagination {
  display: none;
}

.home-seq .seq.seq-active .seq-nav {
  display: block;
}

.home-seq .seq .seq-nav {
  position: absolute;
  z-index: 9999;
  top: 50%;
  width: 100vw;                 /* ✅ 用整個視窗寬 */
  left: 50%;                    /* ✅ 置中參考點 */
  transform: translateX(-50%);  /* ✅ 把 100vw 拉回置中 */

  max-width: none;              /* ✅ 解除 1280 限制 */
  margin: 0 auto;
  padding: 0;
  pointer-events: none;
  border: none;
}

.home-seq .seq .seq-next,
.home-seq .seq .seq-prev {
  position: absolute;
  top: 50%;
  margin-top: -33px;
  width: 35px;
  height: 66px;
  border: none;
  cursor: pointer;
  pointer-events: auto;
  transition-duration: .2s;
  transform: translate3d(0, 0, 0);
  border: 0;
  font: 0/0 a;
  text-shadow: none;
  color: transparent;
}

.home-seq .seq .seq-next {
  right: clamp(20px, 4vw, 64px);
}

.home-seq .seq .seq-next:focus,
.home-seq .seq .seq-next:hover {
  transform: translate3d(4px, 0, 0);
}

.home-seq .seq .seq-prev {
  left: clamp(20px, 4vw, 64px);
}

.home-seq .seq .seq-prev:focus,
.home-seq .seq .seq-prev:hover {
  transform: translate3d(-4px, 0, 0);
}

.home-seq .seq .seq-model,
.home-seq .seq .seq-title {
  position: absolute;
}

.home-seq .seq .seq-title {
  bottom: 5%;
  margin: 0 2.5%;
  width: 95%;
}

.home-seq .seq .seq-model {
  bottom: 15%;
  height: 81%;
  width: 100%;
  max-height: 568px;
  left: 0;
  right: 0;
  margin: 0 auto;
}

.home-seq .seq .seq-model img {
  /* ✅ 你的 b_01~b_04 是橫幅，所以用「容器內完整顯示」 */
  width: 100%;
  height: 100%;
  object-fit: contain;   /* ✅ 不裁切，完整顯示 */
  object-position: center;

  /* ✅ 原版的進出場動畫保留 */
  opacity: 0;
  transform: translate3d(60px, 0, 0);
  transition-duration: .3s;
  transition-properties: transform, opacity;
  transition-timing-function: ease-out !important;

  /* ✅ 原版的 float / margin 對你這種圖反而會害人，拔掉 */
  display: block;
  margin: 0 auto;
  max-height: none;      /* ✅ 不要再卡 568px，交給容器控制 */
}


.home-seq .seq .seq-title br {
  display: none;
}

.home-seq .seq .seq-title h2,
.home-seq .seq .seq-title h3 {
  display: block;
  width: 98%;
  vertical-align: middle;
  text-align: center;
  margin: 0;
  opacity: 0;
  font-size: 16px;
  transition-duration: .3s;
  transition-properties: opacity;
}

.home-seq .seq .seq-title h2 {
  padding: .2em 1%;
  font-family: 'Oxygen', sans-serif;
  color: white;
  background: black;
  float: left;
}

.home-seq .seq .seq-title h3 {
  display: none;
  clear: left;
}

.home-seq .seq .seq-in .seq-model img {
  transform: translate3d(0, 0, 0);
  opacity: 1;
}

.home-seq .seq .seq-in .seq-title h2,
.home-seq .seq .seq-in .seq-title h3 {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.home-seq .seq .seq-out .seq-model img {
  transform: translate3d(-60px, 0, 0);
  opacity: 0;
}

.home-seq .seq .seq-out .seq-title h2,
.home-seq .seq .seq-out .seq-title h3 {
  transform: translate3d(0, 0, 0);
}

@media only screen and (min-width: 640px) {
  .home-seq .seq {
    position: relative;
    height: auto;
    height: 100%;
    max-height: 90vh;
    min-height: 360px;
    background-position: 50% 100%;
  }

  .home-seq .seq:before {
    display: block;
    content: "";
    width: 100%;
    padding-top: 56.25%;
  }

  .home-seq .seq .seq-screen {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .home-seq .seq .seq-canvas > *:before {
    content: "";
    height: 100%;
    display: inline-block;
    vertical-align: middle;
  }

  .home-seq .seq .seq-model,
  .home-seq .seq .seq-title {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    margin: 0 5% 0 2.5%;
  }

  .home-seq .seq .seq-model {
    bottom: 15%;
    height: 81%;
    width: 100%;
    max-height: none;   /* ✅ 你現在不用 568 的限制 */
    margin: 0 auto !important;
    display: block !important;
    left: 0;
    right: 0;
  }


  .home-seq .seq .seq-model img {
    float: none !important;
    opacity: 0;
    transform: translate3d(60px, 0, 0);
    transition-properties: transform, opacity;
  }

  .home-seq .seq .seq-title {
    bottom: 0;
    width: 40%;
    margin-right: 12.5%;
  }

  .home-seq .seq .seq-title h2,
  .home-seq .seq .seq-title h3 {
    display: inline-block;
    width: auto;
    margin: 0;
    opacity: 0;
    text-align: left;
    font-size: 18px;
    transition-duration: .3s;
    transition-properties: transform, opacity;
  }

  .home-seq .seq .seq-title h2 {
    margin-bottom: .4em;
    padding: .2em .6em;
    font-family: 'Oxygen', sans-serif;
    color: white;
    background: black;
    transform: translate3d(-120px, 0, 0);
  }

  .home-seq .seq .seq-title h3 {
    display: block;
    font-family: 'Source Sans Pro', sans-serif;
    transform: translate3d(120px, 0, 0);
  }

  .home-seq .seq .seq-in .seq-model img {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }

  .home-seq .seq .seq-out .seq-model img {
    transform: translate3d(-60px, 0, 0);
  }
}

@media only screen and (min-width: 768px) {
  .home-seq .seq.seq-active .seq-pagination {
    display: block;
  }

  .home-seq .seq .seq-pagination {
    pointer-events: none;
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 10;
    bottom: 5px;
    margin: 0 auto;
    padding: 0 20px;
    max-width: 1240px;
    text-align: right;
  }

  .home-seq .seq .seq-pagination li {
    display: inline-block;
    list-style: none;
    margin: 0 10px;
    padding: 0;
    cursor: pointer;
  }

  .home-seq .seq .seq-pagination li img {
    opacity: .4;
    pointer-events: auto;
    transform: translate3d(0, 0, 0);
    transition-duration: .2s;
    transition-properties: transform, opacity;
  }

  .home-seq .seq .seq-pagination li.seq-current img {
    opacity: 1;
  }

  .home-seq .seq .seq-pagination li:focus img,
  .home-seq .seq .seq-pagination li:hover img {
    transform: translate3d(0, -4px, 0);
  }

  /* ✅ 我們把 <a> 改成 <button>，補一點 reset，視覺不變 */
  .home-seq .seq .seq-pagination button {
    all: unset;
    display: inline-block;
    pointer-events: auto;
    cursor: pointer;
  }
}

@media only screen and (min-width: 860px) {
  .home-seq .seq .seq-model {
    margin-left: 10%;
    width: 27.5%;
  }

  .home-seq .seq .seq-title {
    width: 42.5%;
    margin-right: 10%;
  }

  .home-seq .seq .seq-title br {
    display: inline-block;
  }
}

@media only screen and (min-width: 1020px) {
  .home-seq .seq .seq-model {
    margin-left: 5%;
    width: 32.5%;
  }

  .home-seq .seq .seq-title {
    width: 47.5%;
    margin-right: 5%;
  }
}

@media only screen and (min-width: 1120px) {
  .home-seq .seq .seq-model {
    margin-left: 0;
    width: 37.5%;
  }

  .home-seq .seq .seq-title {
    width: 52.5%;
    margin-right: 0;
  }
}

@media (min--moz-device-pixel-ratio: 1.5), (-o-min-device-pixel-ratio: 3 / 2), (-webkit-min-device-pixel-ratio: 1.5),
  (min-device-pixel-ratio: 1.5), (min-resolution: 144dpi), (min-resolution: 1.5dppx) {
  .home-seq .seq .seq-next {
    background: url("https://raw.githubusercontent.com/SequenceJS/modern-slide-in/ef79a3005818c71087c6abca2cda48f91ff3eb01/images/bt-next@2x.png");
    background-size: 35px 66px;
  }

  .home-seq .seq .seq-prev {
    background: url("https://raw.githubusercontent.com/SequenceJS/modern-slide-in/ef79a3005818c71087c6abca2cda48f91ff3eb01/images/bt-prev@2x.png");
    background-size: 35px 66px;
  }
}

/* =========================================================
   ✅ Section 0：橫幅滿版（2235x800）
   ========================================================= */

/* 1) seq 容器：全寬 + 用正確比例撐高度 */
.home-seq .seq {
  width: 100%;
  max-width: none !important;
  margin: 0 auto;
  position: relative;

  aspect-ratio: 2235 / 800;
  height: auto;

  background-size: cover;
  background-position: center;
}

/* ✅ 手機：用螢幕高度來決定，並限制上下限 */
.home-seq .seq.is-phone {
  aspect-ratio: auto;                 /* ✅ 不要再被 2235/800 綁死 */
  height: clamp(320px, 100vh, 640px);  /* ✅ 你可以調 52vh / 上下限 */
}
.home-seq .seq .seq-screen {
  position: absolute;
  inset: 0;
}


/* 2) 你原本桌機用 :before 撐 16:9，會害比例不對 → 關掉 */
@media only screen and (min-width: 640px) {
  .home-seq .seq:before {
    display: none !important;
    content: none !important;
  }
  .home-seq .seq .seq-screen {
    position: absolute;
    inset: 0;
  }
}

/* 3) seq-model：直接鋪滿整張橫幅，不要再用人物版型的 25% 欄位 */
.home-seq .seq .seq-model {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  bottom: auto !important;
  max-height: none !important;
  display: flex;                 /* ✅ 關鍵 */
  align-items: center;           /* 垂直置中 */
  justify-content: center;       /* 水平置中 */
}

/* ✅ 修正套用 a 連結的 banner 偏左問題：讓 a 標籤也繼承 flex 置中 */
.home-seq .seq .seq-model a {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
  height: 100% !important;
}

/* 4) 大圖：滿版鋪滿（cover） */
.home-seq .seq .seq-model img {
  width: 70% !important;
  height: auto !important;
  max-height: 100%;
  object-fit: contain;           /* 不裁切，穩定 */
  object-position: center;      /* 可改 center bottom 看構圖 */
  display: block !important;
  margin: 0 !important;
  float: none !important;
  max-height: none !important;
}

.home-seq .seq.is-phone .seq-model img {
  width: 90% !important;
  object-fit: contain; /* ✅ 手機比較不裁切重點 */
}

/* ✅ FIX: 讓箭頭永遠在最上層且可點擊 */
.home-seq .seq {
  overflow: visible !important; /* 有時候你改高度後被 overflow hidden 切掉 */
}

/* seq-screen 內的畫面可以裁切，但 nav 不要 */
.home-seq .seq .seq-screen {
  overflow: hidden; /* ✅ 只裁切內容，不裁切箭頭 */
}

/* ✅ 把 nav 提到最高層，並且不要被 transform 影響 */
.home-seq .seq .seq-nav {
  position: absolute !important;
  inset: 0 !important;        /* ✅ 讓它覆蓋整個 banner */
  z-index: 999999 !important; /* ✅ 直接宇宙級 */
  transform: none !important; /* ✅ 不要讓它自成 stacking context */
  pointer-events: none;       /* ✅ 外層不吃點擊 */
}

.home-seq .seq .seq-prev,
.home-seq .seq .seq-next {
  background: transparent !important;
  width: 44px;
  height: 44px;
  border-radius: 0;

  /* 把原本的「文字消音」設定打掉 */
  font: initial !important;
  color: #fff !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.home-seq .seq .seq-prev::before,
.home-seq .seq .seq-next::before {
  font-family: "Material Symbols Outlined";
  font-variation-settings: "opsz" 24, "wght" 400, "FILL" 0, "GRAD" 0;
  font-size: 72px;
  line-height: 1;
  opacity: 0.5;   /* ✅ 50% 透明 */
  color: currentColor;
}

.home-seq .seq .seq-prev::before { content: "keyboard_arrow_left"; }
.home-seq .seq .seq-next::before { content: "keyboard_arrow_right"; }

/* ✅ 把 button 本體文字藏起來（你的 Previous/Next） */
.home-seq .seq .seq-prev,
.home-seq .seq .seq-next {
  text-indent: -9999px;
}
.home-seq .seq .seq-prev::before,
.home-seq .seq .seq-next::before {
  text-indent: 0;
}


/* ✅ Section 0 右下角縮圖：縮小一點 */
.home-seq .seq .seq-pagination li img {
  width: 80px;     /* 你想更小就 36~40 */
  height: 80px;
  object-fit: cover;
  border-radius: 8px; /* 可留可不留，看你喜不喜歡圓角 */
}

/* ✅ 只有 banner 05（fit=cover）才滿版：上下貼合，左右可溢出 */
.home-seq .seq .seq-model img.is-cover {
  width: 100% !important;
  height: 100% !important;     /* 讓它吃滿容器高度 */
  object-fit: cover !important; /* ✅ 上下貼合，左右裁切溢出 */
  object-position: center !important;
}

/* ✅ 只有 banner 05：容器改成視窗滿寬，置中溢出 */
.home-seq .seq .seq-model.is-bleed {
  left: 50% !important;
  right: auto !important;
  top: 0 !important;
  bottom: 0 !important;

  width: 100vw !important;
  height: 100% !important;

  transform: translateX(-50%) !important;
  margin: 0 !important;
}

/* ✅ 只有 banner 05：上下貼合，左右延伸（cover 裁切左右） */
.home-seq .seq .seq-model.is-bleed img.is-cover {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center !important;  /* 想偏右就改 right center */
}

/* ✅ 暫時：解除 Section0 Sequence 的 1280 限寬（讓滿版） */
.home-seq .seq,
.home-seq .seq .seq-canvas,
.home-seq .seq.seq-fallback .seq-canvas {
  max-width: none !important;
}

/* ✅ Canvas 原本 margin:0 auto 也會形成“被置中但不滿版”的視覺，順便拔掉 */
.home-seq .seq .seq-canvas {
  margin: 0 !important;
}

/* ✅ 有些版本會把 screen 或內層也卡在 max-width，保險一起放開 */
.home-seq .seq .seq-screen {
  max-width: none !important;
}



/* =========================
   ✅ Section 1
   ========================= */
.home-hero {
  background: #fff;
}

/* =========================
   ✅ Section 1: Text vibe like screenshot
   ========================= */

/* 右側整塊：限制寬度 + 靠左 + 讓它不要貼齊欄位邊緣 */
.hero-text{
  max-width: 500px;          /* 🔧 截圖感：別太寬，520~560 都合理 */
  margin-left: 0;
  margin-right: auto;
  padding-left: 8px;         /* ✅ 微微內縮，像排版留白 */
  text-align: left;
}

/* 標題：大、乾淨、字距微開、行距舒服（中文也會有質感） */
.hero-title{
  margin: 0 0 20px;
  font-weight: 900;
  font-size: clamp(20px, 2.2vw + 14px, 24px);
  line-height: 1.25;
}

.hero-title-line{
  display: block;
  width: 100%;
  white-space: nowrap;

  /* 每一行各自的 spacing 變數 */
  letter-spacing: var(--ls, 0px);
}

/* S1 標題兩行距離（桌機/手機都吃得到） */
.hero-title-line + .hero-title-line {
  margin-top: 10px; /* 你想更大就加到 12~16 */
}

/* ✅ 英文版：不要硬撐字距，允許自然換行 */
.hero-text.is-en .hero-title-line{
  letter-spacing: normal !important;/* ✅ 關掉外擴 */
  width: auto;                      /* ✅ 不用 100% 撐滿 */
  font-size: clamp(18px, 1.8vw + 12px, 22px) !important;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  padding-left: 0; /* 原本中文有微縮排，英文拿掉 */
}



/* 手機不要撐滿，不然很像字在分手 */
@media (max-width: 576px){
  .hero-title{
    color: #0aa3a3 !important;
  }
  .hero-title-line{
    letter-spacing: 0.06em !important;
  }
}



/* 內文：更窄、行距更高、顏色淡一點 */
.hero-subtitle{
  margin: 0;
  max-width: 500px;          /* ✅ 內文再更窄＝更像海報排版 */
  font-size: 15px;
  line-height: 1.45;         /* ✅ 截圖內文行距偏大 */
  letter-spacing: 0.02em;    /* ✅ 內文很微的字距，閱讀感更柔 */
  color: rgba(0,0,0,0.62);   /* ✅ 不要太黑 */
}

/* 如果你的 subtitle 其實是多段落（v-html 內有 <p>），把段落距離做出來 */
.hero-subtitle p{
  margin: 0 0 14px;
}
.hero-subtitle p:last-child{
  margin-bottom: 0;
}

/* 手機：恢復滿寬，字距收斂一點避免太飄 */
@media (max-width: 576px){
  .hero-text{
    width: 80vw;          /* ✅ 90% 螢幕 */
    max-width: 90vw;      /* ✅ 保險 */
    margin: 0 auto;       /* ✅ 置中，左右留白 */
    padding-left: 0;
  }

  .hero-subtitle{
    max-width: 80vw;      /* ✅ 內文也跟著 90% */
    line-height: 1.85;
  }
  .hero-title{
    letter-spacing: 0.06em;
    line-height: 1.3;
  }
  /* ✅ 手機也吃 JS 算的平鋪字距 */
  .hero-title-line{
    letter-spacing: var(--ls, 0px) !important;
  }
}

/* ✅ (A) 圖片容器：決定「疊圖裁切」與圓角 */
.hero-img-wrap {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 18px;
}

/* ✅ (B) 原本圖片 */
.hero-img {
  width: 100%;
  height: auto;
  display: block;
  position: relative;
  z-index: 1;
}

/* ✅ (C) 軌道疊圖層：整體大小/透明度 */
.hero-orbits.on-image {
  position: absolute;
  inset: -12%;
  z-index: 2;
  pointer-events: none;
  opacity: 0.98;
}

/* =========================
   ✅ (D) Ring 共用：斷續弧段 + 微光（mask 只套 ::before / ::after）
   ========================= */
.hero-orbits .ring {
  position: absolute;
  left: 50%;
  top: 50%;
  border-radius: 999px;
  transform: translate(-50%, -50%);
  pointer-events: none;

  --thickness: 3.2px;

  /* ✅ 主色 #0aa3a3 */
  --c1: rgba(10, 163, 163, 0.62);
  --c2: rgba(10, 163, 163, 0.32);

  opacity: var(--alpha, 1);

  background: none;
  -webkit-mask: none;
  mask: none;

  /* ✅ 每圈用自己的 --dur（你在 r1/r2/r3 設定不同就等於“隨機速度”） */
  animation: ring-rotate var(--dur, 18s) linear infinite;
}

.hero-orbits .ring::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;

  background: conic-gradient(
    from var(--phase, 0deg),
    transparent 0 28deg,
    var(--c1) 28deg 68deg,
    transparent 68deg 122deg,
    var(--c2) 122deg 156deg,
    transparent 156deg 208deg,
    var(--c1) 208deg 250deg,
    transparent 250deg 300deg,
    var(--c2) 300deg 332deg,
    transparent 332deg 360deg
  );

  -webkit-mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--thickness)),
    #000 calc(100% - var(--thickness) + 1px),
    #000 100%
  );
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--thickness)),
    #000 calc(100% - var(--thickness) + 1px),
    #000 100%
  );

  filter:
    blur(0.06px)
    drop-shadow(0 0 8px rgba(10,163,163,0.22))
    drop-shadow(0 0 18px rgba(10,163,163,0.12));
}

.hero-orbits .ring::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;

  background: conic-gradient(
    from calc(var(--phase, 0deg) + 18deg),
    transparent 0 52deg,
    rgba(10,163,163,0.00) 52deg 86deg,
    rgba(10,163,163,0.55) 86deg 98deg,
    rgba(10,163,163,0.42) 98deg 110deg,
    transparent 110deg 200deg,
    rgba(10,163,163,0.42) 200deg 212deg,
    transparent 212deg 360deg
  );

  -webkit-mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--thickness)),
    #000 calc(100% - var(--thickness) + 1px),
    #000 100%
  );
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--thickness)),
    #000 calc(100% - var(--thickness) + 1px),
    #000 100%
  );

  opacity: 0;
  filter: blur(0.10px) drop-shadow(0 0 16px rgba(10,163,163,0.18));

  animation: twinkle var(--tw, 2.7s) steps(1, end) infinite;
  animation-delay: var(--td, 0s);
}

@keyframes twinkle {
  0%, 70%, 100% { opacity: 0; }
  72% { opacity: 0.18; }
  76% { opacity: 0.65; }
  80% { opacity: 0.10; }
  86% { opacity: 0.45; }
  92% { opacity: 0; }
}

.hero-orbits .ring.ccw { animation-direction: reverse; }
.hero-orbits .ring.cw  { animation-direction: normal; }

/* =========================
   ✅ (E) 光點：只有「暈」沒有內核（更柔）
   ========================= */
.hero-orbits .ring .glow-dot {
  position: absolute;
  left: 50%;
  top: 50%;

  transform:
    translate(-50%, -50%)
    rotate(var(--dotA, 20deg))
    translateX(calc(var(--size, 240px) / 2));

  transform-origin: center;
  z-index: 20;

  width: 32px;
  height: 32px;
  border-radius: 999px;

  /* ✅ 沒有白內核：整顆就是霧化光暈 */
  background: radial-gradient(
    circle,
    rgba(10,163,163,0.00) 0%,
    rgba(10,163,163,0.18) 18%,
    rgba(10,163,163,0.34) 40%,
    rgba(10,163,163,0.22) 62%,
    rgba(10,163,163,0.00) 100%
  );

  box-shadow:
    0 0 28px rgba(10,163,163,0.32),
    0 0 72px rgba(10,163,163,0.20),
    0 0 170px rgba(10,163,163,0.12);

  filter: blur(0.35px);
  opacity: 0.9;

  /* ✅ 柔和呼吸 */
  animation: dot-breathe var(--bd, 3.8s) ease-in-out infinite;
}

/* ✅ 不要內核：把 ::before 關掉（保險） */
.hero-orbits .ring .glow-dot::before {
  content: none !important;
}

@keyframes dot-breathe {
  0%, 100% { opacity: 0.78; transform: translate(-50%, -50%) rotate(var(--dotA, 20deg)) translateX(calc(var(--size, 240px) / 2)) scale(1); }
  50%      { opacity: 0.98; transform: translate(-50%, -50%) rotate(var(--dotA, 20deg)) translateX(calc(var(--size, 240px) / 2)) scale(1.07); }
}

/* ✅ (F) 旋轉動畫 */
@keyframes ring-rotate {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
}

/* =========================
   ✅ (G) 每一層：尺寸/速度/微光節奏/光點角度
   ✅ “隨機速度”做法：每圈給不同 --dur（你想更亂就再加幾圈）
   ========================= */
.hero-orbits .r1 { width: 190px; height: 190px; --size: 190px; --thickness: 3.1px; --dur: 11.3s; --phase: 20deg;  --alpha: 1.00; --tw: 2.6s; --td: -0.4s; --dotA: 18deg;  --bd: 4.2s; }
.hero-orbits .r2 { width: 240px; height: 240px; --size: 240px; --thickness: 2.6px; --dur: 16.8s; --phase: 80deg;  --alpha: 0.92; --tw: 3.3s; --td: -1.7s; --dotA: 132deg; --bd: 3.6s; }
.hero-orbits .r3 { width: 290px; height: 290px; --size: 290px; --thickness: 2.8px; --dur: 23.7s; --phase: 140deg; --alpha: 0.88; --tw: 2.9s; --td: -0.9s; --dotA: 260deg; --bd: 4.9s; }

/* =========================
   ✅ (I) 手機縮小：圈縮小 + 光暈也縮小（仍柔）
   ========================= */
@media (max-width: 576px) {
  .hero-orbits.on-image {
    inset: -18%;
    opacity: 0.92;
  }

  .hero-orbits .r1  { width: 150px; height: 150px; --size: 150px; }
  .hero-orbits .r2  { width: 190px; height: 190px; --size: 190px; }
  .hero-orbits .r3  { width: 230px; height: 230px; --size: 230px; }

  .hero-orbits .ring .glow-dot {
    width: 26px;
    height: 26px;
    filter: blur(0.32px);
    box-shadow:
      0 0 24px rgba(10,163,163,0.28),
 0 0 64px rgba(10,163,163,0.18),
      0 0 140px rgba(10,163,163,0.10);
  }
}

/* ✅ (J) 尊重「減少動畫」偏好 */
@media (prefers-reduced-motion: reduce) {
  .hero-orbits .ring { animation: none; }
  .hero-orbits .ring::after { animation: none; opacity: 0; }
  .hero-orbits .ring .glow-dot { animation: none; }
}

/* =========================
   ✅ Section 2: Background Image Band
   ✅ Desktop: title + subtitle on image
   ✅ Mobile : title on image + gray body UNDER image
   ========================= */

/* 外層 section */
.home-text-band {
  overflow: hidden;
}

/* ✅ 上半段：背景圖區 */
.home-text-band .band-hero {
  position: relative;
  display: block;
  width: 100%;

  background-image: url("~@/assets/images/about/myguard_bg_01.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  min-height: 500px;
}

/* ✅ 保險：把內容永遠放在最上層 */
.home-text-band .band-hero .container-sm {
  position: relative;
  z-index: 5;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ✅ 標題（只影響 S2） */
.home-text-band .band-hero .band-title {
  position: relative;
  z-index: 6;
  display: block;

  font-weight: 900;
  font-size: clamp(22px, 2vw + 12px, 30px);
  margin-bottom: 1.25rem;
  color: #fff !important;

  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.35),
    0 0 1px rgba(0, 0, 0, 0.35);
}

/* ✅ 桌機：內文疊字（只影響 S2） */
.home-text-band .band-hero .band-subtitle {
  position: relative;
  z-index: 6;
  display: block;

  max-width: 80vw;
  width: 100%;
  text-align: center;
  font-size: clamp(18px, 1.15vw, 21px);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.95);

  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.30),
    0 0 1px rgba(0, 0, 0, 0.30);
}

/* ✅ v-html 段落間距 */
.home-text-band .band-hero .band-subtitle p {
  margin: 0 0 12px;
}
.home-text-band .band-hero .band-subtitle p:last-child {
  margin-bottom: 0;
}

/* ✅ 下半段：手機灰底內文區（預設先隱藏，避免桌機看到） */
.home-text-band .band-body-mobile {
  display: none;
}

/* =========================
   ✅ Mobile: image top + gray body under it
   ========================= */
@media (max-width: 576px) {
  /* 背景圖高度（你可以微調 300~420） */
  .home-text-band .band-hero {
    min-height: 250px;
  }

  .home-text-band .band-hero .container-sm {
    min-height: 250px;
  }

  /* 手機：背景圖上只留標題，疊字內文隱藏 */
  .home-text-band .band-hero .band-subtitle {
    display: none !important;
    
  }

  /* 手機：灰底區顯示（接在圖下面） */
  .home-text-band .band-body-mobile {
    display: block;
    background: #f2f2f2;
  }

  .home-text-band .band-subtitle-mobile {
    max-width: 80vw;
    margin-left: auto;
    margin-right: auto;
    text-align: left;

    font-size: 17px;
    line-height: 1.6;
    color: rgba(0,0,0,0.62);
  }

  .home-text-band .band-subtitle-mobile p {
    margin: 0 0 14px;
  }
  .home-text-band .band-subtitle-mobile p:last-child {
    margin-bottom: 0;
  }
}
/* =========================================================
   Section 3: Domains
   ========================================================= */

.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: opacity 180ms ease, transform 220ms ease;
}

.fade-slide-up-enter-from,
.fade-slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-up-enter-to,
.fade-slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}


.home-domains {
  background: #fff;

  /* 桌機：下方預留內文空間（domain-pop 用） */
  padding-bottom: 220px;
}

.domains-title {
  font-weight: 800;
  font-size: 1.6rem;
}

.domains-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ✅ 讓內容以整個 Section 為定位基準（桌機 domain-pop 會用到） */
.home-domains .container-sm {
  position: relative;
}

/* 讓超出去的內容真的能顯示出來 */
.home-domains,
.home-domains .container-sm,
.domains-wrap,
.domains-grid {
  overflow: visible;
}

/* ---------- Grid：桌機一排 ---------- */
.domains-grid {
  display: flex;
  justify-content: center;
  align-items: flex-end; /* 只對齊圈圈本體 */
  flex-wrap: nowrap;
  gap: 0;

  /* 圈圈重疊量 */
  --overlap: 9px;
}

/* ---------- 每一顆（圈圈是唯一高度基準） ---------- */
.domain-item {
  position: relative; /* 給 label 絕對定位用 */
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-left: calc(var(--overlap) * -1);
  z-index: 1;
  transition: transform 0.15s ease;
}

.domain-item:first-child {
  margin-left: 0;
}

.domain-item.active {
  z-index: 10;
  transform: translateY(-2px);
}

/* ---------- 常駐標題（不影響圈圈高度） ---------- */
.domain-label {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 140px;
  text-align: center;

  font-weight: 800;
  font-size: 0.95rem;
  line-height: 1.2;
  padding: 4px 2px;
  color: #222;

  white-space: pre-line;
  pointer-events: none;
}

/* 預設：標題在圈圈下方（桌機 + 手機 06~09） */
.domain-item .domain-label {
  top: calc(100% + 10px);
}

/* 手機 02~05：標題在圈圈上方 */
.domain-item.label-above .domain-label {
  top: auto;
  bottom: calc(100% + 10px);
}

/* ---------- 圈圈按鈕本體 ---------- */
.domain-btn {
  position: relative;
  width: 128px; /* 桌機固定圈圈寬 */
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.domain-img {
  width: 100%;
  height: auto;
  display: block;
}

/* 彩色覆蓋層（mask 上色） */
.domain-color {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;

  background-color: var(--activeColor);

  -webkit-mask-image: var(--maskSrc);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center bottom;
  -webkit-mask-size: contain;

  mask-image: var(--maskSrc);
  mask-repeat: no-repeat;
  mask-position: center bottom;
  mask-size: contain;
}

.domain-item.active .domain-color {
  opacity: 1;
}

/* ---------- 桌機：內文跟著選到的那顆 ---------- */
.domain-pop {
  position: absolute;
  z-index: 20;
  left: 50%;
  transform: translateX(-50%);

  width: max-content;
  max-width: none;
  padding: 0;

  top: calc(100% + 76px);
}

.domain-pop-inner {
  width: auto;
  max-width: none;

  text-align: center;
  font-size: 15px;
  line-height: 1.7;
  color: #444;

  padding: 0;
}

/* 桌機留白 */
@media (min-width: 577px) {
  .domain-pop {
    padding: 0 24px;
  }
}

/* =========================================================
   ✅ Mobile: 4 + 4 兩排 + 上/下框「flow 占位」
   ✅ 不用 padding 撐高度
   ========================================================= */
@media (max-width: 576px) {

  /* ✅ 手機：title 永遠最上方 */
  .home-domains .domains-title {
    transform: none !important;
    margin-bottom: 12px;
  }

  /* ✅ 手機：取消桌機的預留空間 */
  .home-domains {
    padding-bottom: 0;
  }

  /* ✅ 手機：關掉桌機那種跟著按鈕的 pop */
  .domain-pop {
    display: none !important;
  }

  /* ✅ Grid 變兩排 */
  .domains-grid {
    flex-wrap: wrap;
    max-width: 440px;
    margin-left: auto;
    margin-right: auto;
    --overlap: 9px;
  }

  .domain-item {
    flex: 0 0 25%;
    max-width: 25%;
    width: 25%;
    flex-shrink: 0;
  }

  .domain-item:nth-child(4n + 1) {
    margin-left: 0;
  }

  .domain-item:not(:nth-child(4n + 1)) {
    margin-left: calc(var(--overlap) * -1);
  }

  .domain-btn {
    width: min(110px, 24vw);
    flex: 0 0 auto;
    flex-shrink: 0;
  }

  .domain-label {
    font-size: 0.7rem;
    max-width: 90px;
  }

  /* ✅ 手機：上下框，用 DOM flow 占位 */
  .domains-pop-box {
    width: min(560px, calc(100% - 24px));
    margin-left: auto;
    margin-right: auto;

    height: auto;

    display: flex;
    align-items: center;
    justify-content: center;

    pointer-events: none;
  }

  .domains-pop-box.is-top {
    margin-top: 6px;
    margin-bottom: 30px;
  }

  .domains-pop-box.is-bottom {
    margin-top: 60px;
    margin-bottom: 0;
  }

  .domains-pop-inner {
    width: 100%;
    max-width: 100%;

    font-size: 15px;
    line-height: 1.35;

    padding: 20px 20px;
    border-radius: 18px;
    border: 3px solid var(--popColor, #170579);
    background: #fff;

    color: var(--popColor, #170579);
    font-weight: 700;
    text-align: center;

    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);

    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 10;
    -webkit-box-orient: vertical;
  }
  /* ✅ 手機：就算上框消失，grid 也不要直接貼 title */
.domains-grid {
  margin-top: 24px; /* ← 這個是保底空氣層 */
}

}


/* =========================================================
   Section 4: Timeline
   ========================================================= */
.home-timeline {
  background: #fff;
}

.timeline-title {
  font-weight: 800;
  font-size: 1.6rem;
  margin-bottom: 2rem;
}

.timeline {
  max-width: 620px;
  position: relative;
  padding-left: 0;
}

.timeline::before {
  content: "";
  position: absolute;
  left: 50px;
  top: 34px;
  bottom: 34px;
  width: 4px;
  background: #19a7ad;
  border-radius: 999px;
}

.timeline-item {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 14px;
  align-items: center;
  padding: 10px 0;
}

.timeline-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeline-year {
  width: 68px;
  height: 68px;
  border-radius: 999px;
  background: #19a7ad;
  color: #fff;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  font-size: 1rem;
  position: relative;
  z-index: 2;
}

/* 不用每段線了，交給 ::before */
.timeline-line {
  display: none;
}

.timeline-content {
  display: flex;
  align-items: center;
}

/* ✅ Timeline 文字強制可換行，避免長字串把版面撐爆 */
.timeline-text {
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  text-align: left;
  width: 100%;
}

/* ✅ v-html 裡面如果有 a 連結，避免 URL 撐爆 */
.timeline-text a {
  overflow-wrap: anywhere;
  word-break: break-word;
}


@media (max-width: 576px) {
  .timeline::before {
    left: 40px;
    top: 28px;
    bottom: 28px;
  }

  .timeline-item {
    grid-template-columns: 80px 1fr;
    gap: 12px;
    padding: 8px 0;
  }

  .timeline-year {
    width: 56px;
    height: 56px;
    font-size: 0.9rem;
  }
}

/* Contact */
#contact {
  p {
    margin: 0;
  }
}

/* ✅ 手機版：Contact 內文 + 地圖都縮到 80% */
@media (max-width: 576px) {
  #contact .container {
    width: 80% !important;     /* ✅ 你要的 80% */
    max-width: 80% !important; /* ✅ 防止 container 自己撐回去 */
    margin-left: auto !important;
    margin-right: auto !important;
  }

  /* ✅ 保險：map 也跟著容器寬度走 */
  #contact iframe {
    width: 100% !important;
    display: block;
  }
}

</style>
